import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import PopupBase from '../../../../popup/base/PopupBase';
import CustomButton from '../../../../components/buttons/CustomButton';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { useWhatsAppSender } from '../../../../contexts/WhatsAppSenderContext';
import { maskPhone, onlyDigits } from '../../../PublicPatientRegistration/registrationMasks';

// Mesmo tom da mensagem da reserva de horário (appointments/v2/utils/reservationMessage.js):
// sem prazo no texto; o link continua valendo VALID_DAYS no backend.
const buildMessage = (clinicName, url) =>
    `${clinicName ? `*${clinicName}*\n\n` : ''}Olá! Para agilizar seu atendimento, preencha seu cadastro neste link:\n${url}`;

/**
 * RegistrationLinkPopup — gera um link de cadastro de uso único e entrega a
 * mensagem pronta para colar na conversa do WhatsApp com o paciente.
 * Com o número próprio de WhatsApp conectado, também envia direto pelo
 * sistema para o telefone informado, com o link em botão.
 * Cada abertura gera um link novo: um link por paciente.
 * Quem gera o link pode marcar de onde a pessoa veio (canal e campanha de
 * marketing); a origem fica no link e o cadastro já nasce com ela.
 */
const selectSx = { fontSize: '13px', borderRadius: '3px' };
const itemStyle = { fontSize: '13px' };

function RegistrationLinkPopup({ onClose }) {
    const {
        CreatePatientRegistrationLink, SendPatientRegistrationLinkWhatsApp, SetPatientRegistrationLinkOrigin,
        GetMarketingChannels, GetMarketingCampaignsByChannel, CompanyConfig,
    } = DataProviderInstance();
    // Envio direto só pelo número próprio: o link nunca sai pelo número da Gabriela
    const { state: waSenderState } = useWhatsAppSender();
    const ownNumberConnected = waSenderState === 'own_connected';

    const [token, setToken] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const [countryCode, setCountryCode] = useState(String(CompanyConfig?.countryCode || '55'));
    const [phone, setPhone] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [sendError, setSendError] = useState('');

    const [channels, setChannels] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [channelId, setChannelId] = useState('');
    const [campaignId, setCampaignId] = useState('');
    const [originError, setOriginError] = useState('');

    useEffect(() => {
        GetMarketingChannels((res) => {
            setChannels(res && res.status === 'success' ? (res.data || []) : []);
        });
        CreatePatientRegistrationLink((res) => {
            if (!res || !res.success || !res.token) {
                setError((res && (res.error || res.message)) || 'Não foi possível gerar o link. Tente novamente.');
                return;
            }
            // Endereço montado com a origem da tela: funciona igual no local e em produção
            const url = `${window.location.origin}/cadastro/${res.token}`;
            setToken(res.token);
            setMessage(buildMessage(CompanyConfig?.Name || '', url));
        });
    }, []);

    // A origem fica gravada no link a cada escolha: nada a confirmar depois
    const saveOrigin = (channel, campaign) => {
        setOriginError('');
        SetPatientRegistrationLinkOrigin(token, channel || null, campaign || null, (res) => {
            if (!res || !res.success) {
                setOriginError((res && res.error) || 'Não foi possível guardar a origem. Tente novamente.');
            }
        });
    };

    const handleChannelChange = (value) => {
        setChannelId(value);
        setCampaignId('');
        setCampaigns([]);
        if (value) {
            GetMarketingCampaignsByChannel(value, (res) => {
                setCampaigns(res && res.status === 'success' ? (res.data || []) : []);
            });
        }
        saveOrigin(value, '');
    };

    const handleCampaignChange = (value) => {
        setCampaignId(value);
        saveOrigin(channelId, value);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(message).then(() => setCopied(true));
    };

    const handleWhatsApp = () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    };

    const phoneDigits = onlyDigits(phone);
    const canSend = !sending && !sent && phoneDigits.length >= 10;

    const handleSend = () => {
        if (!canSend) return;
        setSendError('');
        setSending(true);
        SendPatientRegistrationLinkWhatsApp(token, onlyDigits(countryCode), phoneDigits, (res) => {
            setSending(false);
            if (res && res.success) {
                setSent(true);
                return;
            }
            setSendError((res && res.error) || 'Não foi possível enviar a mensagem. Tente novamente.');
        });
    };

    const sendBlock = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography variant="subtitle2" sx={{ color: ColorPalette.textDark }}>
                Enviar pelo WhatsApp da clínica
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                    label="DDI"
                    size="small"
                    value={countryCode}
                    onChange={(e) => setCountryCode(onlyDigits(e.target.value).slice(0, 3))}
                    disabled={sending || sent}
                    sx={{ width: '80px' }}
                />
                <TextField
                    label="Telefone do paciente"
                    size="small"
                    value={phone}
                    onChange={(e) => setPhone(maskPhone(e.target.value))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                    disabled={sending || sent}
                    inputProps={{ inputMode: 'tel' }}
                    sx={{ flex: '1 1 180px' }}
                />
                <CustomButton
                    variant="contained"
                    style="secondary"
                    label={sent ? 'Enviado' : 'Enviar'}
                    icon={<SendOutlinedIcon />}
                    enabled={canSend}
                    loading={sending}
                    onClick={handleSend}
                />
            </Box>
            {sendError && (
                <Typography variant="body2" color="error">{sendError}</Typography>
            )}
            {sent && (
                <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                    Mensagem enviada com o link em botão. Para outro paciente, gere um novo link.
                </Typography>
            )}
        </Box>
    );

    // Só aparece para quem já cadastrou canais em Marketing
    const originBlock = channels.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box>
                <Typography variant="subtitle2" sx={{ color: ColorPalette.textDark }}>
                    De onde essa pessoa veio
                </Typography>
                <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                    Opcional. O cadastro já entra com esse canal e campanha.
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ flex: '1 1 180px' }}>
                    <InputLabel style={itemStyle}>Canal de marketing</InputLabel>
                    <Select
                        label="Canal de marketing"
                        value={channelId}
                        onChange={(e) => handleChannelChange(e.target.value)}
                        sx={selectSx}
                    >
                        <MenuItem value="" style={itemStyle}>Sem canal</MenuItem>
                        {channels.map((channel) => (
                            <MenuItem key={channel.id} value={channel.id} style={itemStyle}>{channel.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {channelId && (
                    <FormControl size="small" sx={{ flex: '1 1 180px' }}>
                        <InputLabel style={itemStyle}>Campanha de marketing</InputLabel>
                        <Select
                            label="Campanha de marketing"
                            value={campaignId}
                            onChange={(e) => handleCampaignChange(e.target.value)}
                            sx={selectSx}
                        >
                            <MenuItem value="" style={itemStyle}>Sem campanha</MenuItem>
                            {campaigns.map((campaign) => (
                                <MenuItem key={campaign.id} value={campaign.id} style={itemStyle}>{campaign.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>
            {originError && (
                <Typography variant="body2" color="error">{originError}</Typography>
            )}
        </Box>
    );

    const content = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
            <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                {ownNumberConnected
                    ? 'Informe o telefone e o paciente recebe a mensagem com o link na hora. Ele preenche os dados e já entra na sua lista de pacientes.'
                    : 'Envie esta mensagem na conversa com o paciente. Ele preenche os dados e já entra na sua lista de pacientes.'}
            </Typography>

            {!message && !error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {error && (
                <Typography variant="body2" color="error">{error}</Typography>
            )}

            {message && (
                <>
                    {originBlock}

                    {ownNumberConnected && sendBlock}

                    {ownNumberConnected && (
                        <Typography variant="subtitle2" sx={{ color: ColorPalette.textDark }}>
                            Ou copie a mensagem
                        </Typography>
                    )}
                    <TextField
                        value={message}
                        multiline
                        minRows={5}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        onFocus={(e) => e.target.select()}
                    />
                    <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <CustomButton
                            variant="outlined"
                            style="secondary"
                            label={copied ? 'Mensagem copiada' : 'Copiar mensagem'}
                            icon={<ContentCopyOutlinedIcon />}
                            onClick={handleCopy}
                        />
                        <CustomButton
                            variant={ownNumberConnected ? 'outlined' : 'contained'}
                            style="secondary"
                            label="Abrir no WhatsApp"
                            icon={<WhatsAppIcon />}
                            onClick={handleWhatsApp}
                        />
                    </Box>
                </>
            )}
        </Box>
    );

    return (
        <PopupBase
            Title="Link de cadastro do paciente"
            Content={content}
            OnClose={onClose}
            maxWidth="sm"
        />
    );
}

export default RegistrationLinkPopup;
